export type CasePanelMode = 'edit' | 'ask'

const STORAGE_PREFIX = 'caseysPanel.casePanelMode'
// Pre-per-workspace key. No longer written, but a browser that saved under it before
// this change must not keep influencing reads for any workspace.
const LEGACY_GLOBAL_KEY = STORAGE_PREFIX
const TTL_MS = 24 * 60 * 60 * 1000

const storageKey = (workspaceId: string): string => `${STORAGE_PREFIX}.${workspaceId}`

/** Warn once about a malformed (not merely expired) entry, then discard it. */
const discardMalformedEntry = (key: string, reason: string): void => {
  console.warn(`[casePanelMode] Discarding malformed stored mode: ${reason}`)
  try {
    localStorage.removeItem(key)
  } catch (err) {
    console.warn('[casePanelMode] Failed to remove malformed entry:', err)
  }
}

/**
 * Read the physician's last Edit/Ask choice for this workspace. Resolves to 'edit' when
 * the workspace id is falsy, or the entry is absent, expired, malformed, or unreadable —
 * Edit is the default the spec fixes, and it is also the safe fallback (a stale Ask would
 * silently mute a new session's canvas writes). Every workspace the physician has never
 * set a mode in starts in Edit; it never inherits another workspace's choice. A malformed
 * entry (bad expiry, illegal mode, or invalid JSON) is removed so it doesn't keep tripping
 * this fallback on every future read. An unreadable store (access denied, e.g. Safari
 * private mode) is left untouched — a blocked store can't be written to either, so removal
 * would just throw again. A merely expired entry is also left in place and handled
 * silently — that's the normal TTL path.
 */
export const loadCasePanelMode = (workspaceId: string): CasePanelMode => {
  if (!workspaceId) return 'edit'
  const key = storageKey(workspaceId)

  let stored: string | null
  try {
    stored = localStorage.getItem(key)
  } catch (err) {
    console.warn('[casePanelMode] Storage unreadable, defaulting to edit:', err)
    return 'edit'
  }
  if (!stored) return 'edit'

  try {
    const { mode, expiresAt } = JSON.parse(stored)

    if (typeof expiresAt !== 'number') {
      discardMalformedEntry(key, 'expiresAt is missing or not a number')
      return 'edit'
    }
    if (expiresAt <= Date.now()) return 'edit'

    if (mode !== 'ask' && mode !== 'edit') {
      discardMalformedEntry(key, `mode "${mode}" is not a legal interactive mode`)
      return 'edit'
    }
    return mode
  } catch (err) {
    discardMalformedEntry(key, `unreadable entry: ${err}`)
    return 'edit'
  }
}

/** Every key currently in storage, or `[]` if the store can't be enumerated. */
const collectStorageKeys = (): string[] => {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) keys.push(key)
    }
    return keys
  } catch {
    return []
  }
}

/**
 * Remove the legacy pre-per-workspace key and any sibling workspace entries whose TTL has
 * already elapsed — both are dead weight that must stop influencing future reads. Bounded
 * to the keys present at call time; a failure on any one key (blocked storage, concurrent
 * mutation) is swallowed so it never surfaces as the save's own failure.
 */
const pruneStaleEntries = (): void => {
  try {
    localStorage.removeItem(LEGACY_GLOBAL_KEY)
  } catch {
    // best-effort; a store that can't be written to can't be pruned either
  }

  const siblingKeys = collectStorageKeys().filter(k => k.startsWith(`${STORAGE_PREFIX}.`))
  for (const key of siblingKeys) {
    try {
      const stored = localStorage.getItem(key)
      if (!stored) continue
      const { expiresAt } = JSON.parse(stored)
      if (typeof expiresAt === 'number' && expiresAt <= Date.now()) {
        localStorage.removeItem(key)
      }
    } catch {
      // malformed or inaccessible sibling — left for its own future load() to discard
    }
  }
}

/**
 * Persist the choice for this workspace, refreshing its 24h expiry, then prune the legacy
 * global key and any expired sibling workspace entries. Write failures are non-fatal. A
 * falsy workspace id is a no-op — there is nothing to scope the save to.
 */
export const saveCasePanelMode = (workspaceId: string, mode: CasePanelMode): void => {
  if (!workspaceId) return
  try {
    localStorage.setItem(
      storageKey(workspaceId),
      JSON.stringify({ mode, expiresAt: Date.now() + TTL_MS })
    )
  } catch (err) {
    console.warn('[casePanelMode] Failed to persist mode:', err)
    return
  }
  pruneStaleEntries()
}
